import { useState, useEffect } from 'react'
import LoginPage from './components/LoginPage'
import AcceptInvitePage from './components/AcceptInvitePage'
import RequestPasswordResetPage from './components/RequestPasswordResetPage'
import ConfirmPasswordResetPage from './components/ConfirmPasswordResetPage'
import StatusPage from './pages/StatusPage'
import DeviceTimelinePage from './pages/DeviceTimelinePage'
import CapturesPage from './pages/CapturesPage'
import FishbrainExportPage from './pages/FishbrainExportPage'
import SubmissionsPage from './pages/SubmissionsPage'
import SettingsPage from './pages/SettingsPage'
import UploadPage from './pages/UploadPage'
import LegalPage from './components/LegalPage'
import LandingPage from './components/LandingPage'
import { Api } from './api'

type Route =
  | { page: 'captures'; hostname?: string; captureId?: string; filterMode?: string; showNoFish?: boolean }
  | { page: 'status' }
  | { page: 'device-timeline'; hostname: string; runId?: string }
  | { page: 'fishbrain-export' }
  | { page: 'submissions' }
  | { page: 'settings' }
  | { page: 'upload' }
  | { page: 'accept-invite' }
  | { page: 'reset-password' }
  | { page: 'legal' }
  | { page: 'login' }

function getRoute(): Route {
  // The upload page is a self-contained front door with its own session
  // check (Upload.me()) — independent of the app's `authed` state/chrome.
  if (window.location.pathname === '/upload') return { page: 'upload' }
  if (window.location.pathname === '/legal') return { page: 'legal' }
  if (window.location.pathname === '/login') {
    // Clean entry URL for sign-in; normalize to hash routing so post-login
    // hash navigation isn't pinned to the /login pathname.
    window.history.replaceState({}, '', '/#login')
    return { page: 'login' }
  }
  const hash = window.location.hash
  if (hash === '#legal') return { page: 'legal' }
  if (hash === '#login') return { page: 'login' }
  if (hash === '#accept-invite') return { page: 'accept-invite' }
  if (hash === '#reset-password') return { page: 'reset-password' }
  if (hash === '#status') return { page: 'status' }
  if (hash === '#fishbrain-export') return { page: 'fishbrain-export' }
  if (hash === '#settings') return { page: 'settings' }
  // '#mms…' kept as an alias — old links in the team WhatsApp feed still open.
  if (hash === '#submissions' || hash.startsWith('#submissions?')) return { page: 'submissions' }
  if (hash === '#mms' || hash.startsWith('#mms?')) return { page: 'submissions' }
  if (hash.startsWith('#device/')) {
    const fullTail = decodeURIComponent(hash.slice('#device/'.length))
    const tail = fullTail.includes('?') ? fullTail.slice(0, fullTail.indexOf('?')) : fullTail
    const qs = fullTail.includes('?') ? new URLSearchParams(fullTail.slice(fullTail.indexOf('?') + 1)) : null
    const hostname = tail.endsWith('/timeline') ? tail.slice(0, -'/timeline'.length) : tail
    return { page: 'device-timeline', hostname, runId: qs?.get('run_id') ?? undefined }
  }
  if (hash.startsWith('#capture=')) {
    return { page: 'captures' }
  }
  if (hash.startsWith('#captures')) {
    const qs = hash.includes('?') ? new URLSearchParams(hash.slice(hash.indexOf('?') + 1)) : null
    return {
      page: 'captures',
      hostname: qs?.get('hostname') ?? undefined,
      captureId: qs?.get('capture_id') ?? undefined,
      filterMode: qs?.get('filter') ?? undefined,
      showNoFish: qs?.get('no_fish') === '1' ? true : undefined,
    }
  }
  return { page: 'status' }
}

export default function App() {
  const urlParams = new URLSearchParams(window.location.search)
  const token = urlParams.get('token')
  const googleAuthError = urlParams.get('google_auth_error')

  const [errorBanner, setErrorBanner] = useState<string | null>(() => {
    if (!googleAuthError) return null
    window.history.replaceState({}, '', window.location.pathname + window.location.hash)
    return googleAuthError === 'email_mismatch'
      ? 'Your Google account email doesn\'t match the invite address. Email aliases (e.g. email+alias@gmail.com) must use password login.'
      : 'Google sign-in failed.'
  })

  const [authed, setAuthed] = useState<boolean | null>(null)
  const [route, setRoute]   = useState<Route>(getRoute)
  const [loginRedirect, setLoginRedirect] = useState<string | null>(null)

  useEffect(() => {
    Api.getCurrentUser()
      .then(() => setAuthed(true))
      .catch(() => {
        const hash = window.location.hash
        if (hash && hash !== '#status' && hash !== '') setLoginRedirect(hash)
        setAuthed(false)
      })
  }, [])

  useEffect(() => {
    const onHash = () => setRoute(getRoute())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  function navigate(target: string) {
    window.location.hash = target
    setRoute(getRoute())
  }

  async function handleLogin(username: string, password: string): Promise<void> {
    await Api.loginWithPassword({ username, password })
    setAuthed(true)
    navigate(loginRedirect ?? '#status')
    setLoginRedirect(null)
  }

  async function handleGoogleLogin(): Promise<void> {
    const { url } = await Api.getGoogleLoginUrl({ redirectUrl: loginRedirect ?? '' })
    window.location.href = url
  }

  function handlePhoneLogin(): void {
    setAuthed(true)
    navigate(loginRedirect ?? '#status')
    setLoginRedirect(null)
  }

  async function handleLogout() {
    try { await Api.logout() } catch {}
    setAuthed(false)
  }

  const banner = errorBanner && (
    <div className="fixed top-0 inset-x-0 z-50 flex items-center justify-between gap-4
      bg-coral/10 border-b border-coral/30 px-4 py-3">
      <p className="font-mono text-[11px] tracking-[0.1em] text-coral">{errorBanner}</p>
      <button
        type="button"
        onClick={() => setErrorBanner(null)}
        className="font-mono text-[11px] text-coral/60 hover:text-coral transition-colors shrink-0 cursor-pointer"
      >
        dismiss
      </button>
    </div>
  )

  if (route.page === 'upload') {
    return <UploadPage />
  }

  if (route.page === 'legal') {
    return <LegalPage />
  }

  if (route.page === 'accept-invite') {
    return <>{banner}<AcceptInvitePage token={token} /></>
  }

  if (route.page === 'reset-password') {
    return token
      ? <ConfirmPasswordResetPage token={token} />
      : <RequestPasswordResetPage />
  }

  if (authed === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="font-mono text-xs tracking-[0.2em] uppercase text-stone-400 animate-pulse">
          Loading…
        </p>
      </div>
    )
  }

  if (!authed) {
    // Deep links (invite emails, shared capture URLs) go straight to sign-in.
    // Bare visits to / get the public landing page only on the prod host:
    // Twilio's A2P registration cites app.kodiaklabs.com and requires the SMS
    // program info to be readable with no login. Dev and local go to sign-in.
    const publicLanding = window.location.hostname === 'app.kodiaklabs.com'
    if (route.page === 'login' || loginRedirect || !publicLanding) {
      return <>{banner}<LoginPage onLogin={handleLogin} onGoogleSignIn={handleGoogleLogin} onPhoneLogin={handlePhoneLogin} /></>
    }
    return <>{banner}<LandingPage /></>
  }

  if (route.page === 'login') {
    return <>{banner}<StatusPage onNavigate={navigate} onLogout={handleLogout} /></>
  }

  if (route.page === 'status') {
    return <>{banner}<StatusPage onNavigate={navigate} onLogout={handleLogout} /></>
  }

  if (route.page === 'fishbrain-export') {
    return <>{banner}<FishbrainExportPage onNavigate={navigate} onLogout={handleLogout} /></>
  }

  if (route.page === 'submissions') {
    return <>{banner}<SubmissionsPage onNavigate={navigate} onLogout={handleLogout} /></>
  }

  if (route.page === 'settings') {
    return <>{banner}<SettingsPage onNavigate={navigate} onLogout={handleLogout} /></>
  }

  if (route.page === 'device-timeline') {
    return (
      <>{banner}<DeviceTimelinePage
        key={route.hostname}
        hostname={route.hostname}
        initialRunId={route.runId}
        onNavigate={navigate}
        onLogout={handleLogout}
      /></>
    )
  }

  return (
    <>{banner}<CapturesPage
      onLogout={handleLogout}
      onNavigate={navigate}
      hostname={route.page === 'captures' ? route.hostname : undefined}
      captureId={route.page === 'captures' ? route.captureId : undefined}
      filterMode={route.page === 'captures' ? route.filterMode : undefined}
      showNoFish={route.page === 'captures' ? route.showNoFish : undefined}
    /></>
  )
}
